import {
  Button,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useCallback, useMemo, useState } from "react";
import { defaults } from "../core/utils";
import useEmbedParameters, {
  EmbedParameters,
  embedParametersToHTML,
  embedParametersToURL,
} from "../hooks/useEmbedParameters";
import Heading from "./Heading";

interface SetupToolProps {}

export default function SetupTool(_props: SetupToolProps): ReactElement {
  const initial = useEmbedParameters();
  const [parameters, setParameters] = useState<EmbedParameters>(() =>
    defaults(
      { mapboxToken: "", configUrl: "", csvUrl: "", spreadsheet: "" },
      initial
    )
  );

  const onDatasetURLChange = useCallback(
    (url: string) => {
      const googleSheetsPrefix = "https://docs.google.com/spreadsheets/d/";
      if (url.startsWith(googleSheetsPrefix)) {
        setParameters({
          ...parameters,
          csvUrl: "",
          spreadsheet: url
            .slice(googleSheetsPrefix.length)
            .replace(/\/.*$/, ""),
        });
      } else {
        setParameters({ ...parameters, spreadsheet: "", csvUrl: url });
      }
    },
    [parameters]
  );
  const shareLink = useMemo(
    () => embedParametersToURL(parameters),
    [parameters]
  );
  const shareHTML = useMemo(
    () => embedParametersToHTML(parameters),
    [parameters]
  );

  const copyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  return (
    <VStack maxW="32rem" align="flex-start" spacing="8" h="100%">
      <Heading mt="8" level={3}>
        Map Embed Setup
      </Heading>
      <FormControl>
        <FormLabel>Mapbox Token</FormLabel>
        <Input
          type="text"
          onChange={(evt) =>
            setParameters({ ...parameters, mapboxToken: evt.target.value })
          }
        />
        <FormHelperText>
          You'll need a{" "}
          <Link href="https://www.mapbox.com/" isExternal={true}>
            mapbox
          </Link>{" "}
          account for this. Once you create an account, go to your{" "}
          <Link
            color="blue.400"
            href="https://account.mapbox.com/"
            isExternal={true}
          >
            account page
          </Link>{" "}
          and click "Create a token". Then leave the defaults as they are (all
          public scopes, but no secret scopes checked) And click Create token.
          Then copy the token code in to the field above.
        </FormHelperText>
      </FormControl>
      <FormControl>
        <FormLabel>Dataset URL</FormLabel>
        <Input
          type="url"
          onChange={(evt) => onDatasetURLChange(evt.target.value)}
        />
        <FormHelperText>
          The link to your dataset. This can be a google sheet, or a CSV source.
          To find out more on how to create a dataset, use{" "}
          <Link
            color="blue.400"
            href="https://github.com/eniallator/Mapping-Embed-Configurations#dataset-specification"
            isExternal={true}
          >
            this documentation
          </Link>
        </FormHelperText>
      </FormControl>
      <FormControl>
        <FormLabel>Config URL</FormLabel>
        <Input
          type="text"
          onChange={(evt) =>
            setParameters({ ...parameters, configUrl: evt.target.value })
          }
        />
        <FormHelperText>
          The URL of embed's configuration. To find out more on this, there's{" "}
          <Link
            color="blue.400"
            href="https://github.com/eniallator/Mapping-Embed-Configurations#creating-a-dataset-config"
            isExternal={true}
          >
            some documentation
          </Link>{" "}
          describing how to create one from scratch.
        </FormHelperText>
      </FormControl>
      <Tabs isLazy={true} isFitted={true} w="100%" pb="8">
        <TabList>
          <Tab>Shareable Link</Tab>
          <Tab>HTML Code</Tab>
        </TabList>
        <TabPanels>
          <TabPanel
            display="flex"
            flexDir="column"
            alignItems="flex-start"
            gap="2"
          >
            <Link
              color="blue.400"
              href={shareLink}
              maxW="100%"
              isExternal={true}
            >
              {shareLink}
            </Link>
            <Button
              size="sm"
              variant="outline"
              bg="white"
              onClick={() => copyText(shareLink)}
            >
              Copy Link
            </Button>
          </TabPanel>
          <TabPanel
            display="flex"
            flexDir="column"
            alignItems="flex-start"
            gap="2"
          >
            <Code
              display="block"
              maxW="100%"
              whiteSpace="pre"
              px="4"
              py="2"
              overflowX="auto"
            >
              {shareHTML}
            </Code>
            <Button
              size="sm"
              variant="outline"
              bg="white"
              onClick={() => copyText(shareHTML)}
            >
              Copy HTML
            </Button>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
